import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBolt,
  FaBuilding,
  FaCheckCircle,
  FaLaptopCode,
  FaLock,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaRegClock,
} from "react-icons/fa";
import { Fade } from "react-reveal";

import Footer from "../Common/Footer";
import Header from "../Common/Header";
import { CareerEvent, careerApi } from "../../../api/career";

type WorkKind = "vacancy" | "instant" | "digital";
type WorkFilter = "all" | WorkKind;

const jobCategories: Array<{
  title: string;
  text: string;
  value: WorkFilter;
  icon: typeof FaBolt;
}> = [
  {
    title: "Миттєва робота",
    text: "Разові задачі та швидкий старт.",
    value: "instant",
    icon: FaBolt,
  },
  {
    title: "Усі пропозиції",
    text: "Партнерські пропозиції і графік.",
    value: "all",
    icon: FaBuilding,
  },
  {
    title: "Цифрова робота",
    text: "Онлайн-задачі, Canva, документи.",
    value: "digital",
    icon: FaLaptopCode,
  },
];

const splitJobText = (value?: string) =>
  String(value || "")
    .split(/\r?\n|;|•|▫️|✅/)
    .map((item) => item.trim().replace(/^[-–—]\s*/, ""))
    .filter(Boolean);

const getPublicJobKind = (job: CareerEvent): WorkKind => {
  if (job.isDigitalWork) return "digital";
  if (job.isInstantWork || job.kind === "quick_work") return "instant";
  return "vacancy";
};

const getPublicJobCategory = (kind: WorkKind) => {
  if (kind === "digital") return "Цифрова робота";
  if (kind === "instant") return "Миттєва робота";
  return "Усі пропозиції";
};

const WorkComponent = () => {
  const [publicJobs, setPublicJobs] = useState<CareerEvent[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<WorkFilter>("all");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    careerApi
      .listPublicJobs()
      .then(setPublicJobs)
      .catch(() => setPublicJobs([]));
  }, []);

  const allJobsToRender = publicJobs.map((job, index) => {
    const kind = getPublicJobKind(job);

    return {
      id: `${job._id}-${job.title}`,
      kind,
      category: getPublicJobCategory(kind),
      title: job.title,
      company: job.company || "Партнерська організація",
      salary: job.payout || "за результатами співбесіди",
      location: job.location || job.formatLabel || "локація уточнюється",
      schedule: job.schedule || job.startLabel || "графік уточнюється",
      description: job.description || "Деталі вакансії уточнюються.",
      responsibilities: splitJobText(job.responsibilities),
      benefits: splitJobText(job.benefits),
      requirements: splitJobText(job.requirements),
      hasReservation: Boolean(job.hasReservation),
      tags: job.tags || [],
      contact: "Контакти доступні після реєстрації",
      index: index + 1,
    };
  });
  const filteredJobs = allJobsToRender.filter((job) => {
    const categoryMatches =
      selectedCategory === "all" ? true : job.kind === selectedCategory;

    return categoryMatches;
  });

  return (
    <section className="min-h-screen bg-[#f4f7fb] text-black">
      <Header />

      <main>
        <section className="relative isolate overflow-hidden bg-black">
          <div
            className="absolute inset-0 opacity-35"
            style={{
              backgroundImage:
                "linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.82)), url('/assets/Images/bg_img/intro_1.png')",
              backgroundPosition: "center",
              backgroundSize: "cover",
            }}
          />
          <div className="absolute right-0 top-0 hidden h-full w-[42%] bg-[repeating-linear-gradient(135deg,rgba(255,212,0,0.22)_0,rgba(255,212,0,0.22)_2px,transparent_2px,transparent_16px)] lg:block" />

          <div className="container relative z-10 mx-auto px-4 py-12 sm:py-16 lg:py-20">
            <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
              <Fade bottom>
                <div>
                  <div className="mb-5 inline-flex items-center gap-3 rounded-full border-2 border-[#ffd400] bg-[#ffd400] px-5 py-2 text-xs font-black uppercase tracking-[0.16em] text-black shadow-[4px_4px_0_#000]">
                    <FaBolt />
                    Робота поруч
                  </div>
                  <h1 className="max-w-[900px] text-[40px] font-extrabold leading-[1.02] tracking-[-0.025em] text-white sm:text-[58px] lg:text-[74px]">
                    Знайди роботу сьогодні - почни заробляти завтра
                  </h1>
                  <p className="mt-6 max-w-[720px] text-[16px] font-medium leading-7 text-white/85 sm:text-[18px]">
                    Допоможемо підібрати роботу під твій досвід, графік і місто.
                  </p>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <Link
                      to="/registerr"
                      className="inline-flex items-center justify-center gap-3 rounded-full border border-[#ffd400] bg-[#ffd400] px-7 py-3.5 text-sm font-bold text-black shadow-[0_8px_22px_rgba(255,212,0,0.25)] transition hover:-translate-y-0.5"
                    >
                      Залишити заявку
                      <FaArrowRight />
                    </Link>
                    <a
                      href="#job-list"
                      className="inline-flex items-center justify-center gap-3 rounded-full border border-white/50 bg-white/5 px-7 py-3.5 text-sm font-bold text-white transition hover:bg-white hover:text-black"
                    >
                      Дивитися вакансії
                    </a>
                  </div>
                </div>
              </Fade>

              <Fade bottom>
                <div className="rounded-[26px] border border-white/20 bg-white p-6 shadow-[0_18px_45px_rgba(0,0,0,0.22)]">
                  <div className="flex items-center gap-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-[16px] bg-[#ffd400] text-xl">
                      <FaMoneyBillWave />
                    </span>
                    <div>
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#9b7a00]">
                        Швидкий старт
                      </p>
                      <h2 className="text-[22px] font-extrabold leading-none">
                        Робота прямо зараз
                      </h2>
                    </div>
                  </div>
                  <div className="mt-6 space-y-4 text-sm font-medium text-black/70">
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="text-[#16a34a]" />
                      Підбір задач під твій графік
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="text-[#16a34a]" />
                      Короткий брифінг перед стартом
                    </div>
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="text-[#16a34a]" />
                      Можливість швидкої виплати після виконання
                    </div>
                  </div>
                </div>
              </Fade>
            </div>
          </div>
        </section>

        <section className="bg-white pt-8 pb-5 md:pt-10">
          <div className="container mx-auto px-4">
            <Fade bottom>
              <div className="mx-auto max-w-2xl text-center">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#9b7a00]">
                  Напрями роботи
                </p>
                <h2 className="mt-2 text-[28px] font-extrabold leading-none text-black sm:text-[36px]">
                  Обери свій формат
                </h2>
              </div>
            </Fade>

            <div className="mx-auto mt-7 grid max-w-[820px] grid-cols-3 gap-4 sm:gap-8">
              {jobCategories.map((category) => {
                const Icon = category.icon;
                const isActive = selectedCategory === category.value;

                return (
                  <Fade bottom key={category.title}>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedCategory(isActive ? "all" : category.value)
                      }
                      className="group flex flex-col items-center text-center"
                    >
                      <span
                        className={`grid h-20 w-20 place-items-center rounded-full border text-[28px] shadow-[0_10px_24px_rgba(15,23,42,0.14)] transition group-hover:-translate-y-1 sm:h-24 sm:w-24 sm:text-[32px] ${
                          isActive
                            ? "border-black bg-[#ffd400] text-black"
                            : "border-slate-200 bg-white text-[#8a6f00] group-hover:border-[#ffd400] group-hover:bg-[#ffd400] group-hover:text-black"
                        }`}
                      >
                        <Icon />
                      </span>
                      <span className="mt-3 block text-[13px] font-semibold leading-4 text-slate-800 sm:text-[15px]">
                        {category.title}
                      </span>
                    </button>
                  </Fade>
                );
              })}
            </div>
          </div>
        </section>

        <section id="job-list" className="bg-[#f4f7fb] py-8 md:py-10 lg:py-12">
          <div className="container mx-auto px-4">
            <Fade bottom>
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#9b7a00]">
                    Рекомендовані пропозиції
                  </p>
                  <h2 className="mt-2 text-[34px] font-extrabold leading-none tracking-[-0.02em] text-black sm:text-[46px]">
                    Актуальна робота
                  </h2>
                </div>
                <div className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-bold text-slate-700">
                  Знайдено: {filteredJobs.length}
                </div>
              </div>
            </Fade>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredJobs.map((job) => (
                <article
                  key={job.id}
                  className="group flex h-full flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.07)] transition hover:-translate-y-1 hover:border-[#d8bd28]"
                >
                  <div className="h-2 bg-[#ffd400]" />
                  <div className="flex flex-1 flex-col p-5 sm:p-6">
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#fff6c8] px-3 py-1.5 text-[11px] font-semibold text-[#665000]">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#ffd400] text-[10px] text-black">
                          {job.kind === "instant" ? (
                            job.index
                          ) : job.kind === "digital" ? (
                            <FaLaptopCode />
                          ) : (
                            <FaBuilding />
                          )}
                        </span>
                        {job.category}
                      </span>
                      <span className="max-w-[170px] truncate rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-500">
                        {job.company}
                      </span>
                      {job.hasReservation && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
                          <FaCheckCircle className="text-[10px]" />
                          Бронювання
                        </span>
                      )}
                    </div>

                    <h3 className="text-[22px] font-extrabold leading-[1.12] tracking-[-0.012em] text-black">
                      {job.title}
                    </h3>
                    <p className="mt-3 text-[14px] font-medium leading-6 text-slate-600">
                      {job.description}
                    </p>

                    <div className="mt-5 rounded-[20px] bg-[#faf7ed] p-4">
                      <div className="flex items-start gap-3">
                        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-white text-[#8a6f00]">
                          <FaMoneyBillWave />
                        </span>
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8a6f00]">
                            Оплата
                          </p>
                          <p className="mt-1 text-[17px] font-extrabold leading-5 text-black">
                            {job.salary}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-2 text-[13px] font-semibold text-slate-700">
                        <div className="flex items-start gap-2">
                          <FaRegClock className="mt-1 shrink-0 text-[#8a6f00]" />
                          <span>{job.schedule}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <FaMapMarkerAlt className="mt-1 shrink-0 text-[#8a6f00]" />
                          <span>{job.location}</span>
                        </div>
                      </div>
                    </div>

                    {(job.responsibilities.length > 0 ||
                      job.benefits.length > 0 ||
                      job.requirements.length > 0) && (
                      <div className="mt-5 rounded-[20px] border border-slate-200 bg-white px-4 py-4">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
                          Деталі вакансії
                        </p>
                        {[
                          {
                            title: "Що робити",
                            items: job.responsibilities,
                          },
                          {
                            title: "Що пропонуємо",
                            items: job.benefits,
                          },
                          {
                            title: "Вимоги",
                            items: job.requirements,
                          },
                        ]
                          .filter((section) => section.items.length > 0)
                          .map((section) => (
                            <div
                              key={section.title}
                              className="border-b border-slate-100 py-3 last:border-b-0 last:pb-0"
                            >
                              <p className="mb-2 text-[12px] font-extrabold text-black">
                                {section.title}
                              </p>
                              <ul className="grid gap-1.5">
                                {section.items.slice(0, 4).map((item) => (
                                  <li
                                    key={item}
                                    className="flex gap-2 text-[13px] font-medium leading-5 text-slate-600"
                                  >
                                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8a6f00]" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                      </div>
                    )}

                    {job.tags.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        {job.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full border border-[#ead98a] bg-[#fff6c8] px-3 py-1 text-[11px] font-medium text-[#665000]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-auto pt-5">
                      {job.contact ? (
                        <div className="rounded-[18px] border border-dashed border-[#d8c56b] bg-[#fffdf7] p-3">
                          <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a6f00]">
                            <FaLock />
                            Контакти роботодавця
                          </div>
                          <div className="relative overflow-hidden rounded-[13px] border border-slate-200 bg-white px-3 py-3">
                            <div className="select-none blur-[4px]">
                              <div className="flex items-center gap-2 text-sm font-semibold text-black">
                                <FaPhoneAlt />
                                {job.contact}
                              </div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center bg-white/55 text-center text-[11px] font-bold tracking-[0.08em] text-black">
                              після реєстрації
                            </div>
                          </div>
                        </div>
                      ) : null}

                      <Link
                        to="/registerr"
                        className="mt-4 inline-flex w-full items-center justify-center gap-3 rounded-full border-2 border-black bg-black px-5 py-3 text-sm font-bold text-white shadow-[3px_3px_0_#ffd400] transition group-hover:bg-[#ffd400] group-hover:text-black"
                      >
                        Відгукнутись
                        <FaArrowRight />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}

              {!filteredJobs.length && (
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-white p-8 text-center">
                  <p className="text-lg font-black text-black">
                    Нічого не знайшли за цими фільтрами.
                  </p>
                  <p className="mt-2 text-sm font-semibold text-slate-500">
                    Спробуй змінити запит або повернути всі пропозиції.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSelectedCategory("all")}
                    className="mt-5 rounded-full border-2 border-black bg-[#ffd400] px-6 py-3 text-sm font-black uppercase text-black shadow-[3px_3px_0_#000]"
                  >
                    Скинути фільтри
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="bg-white py-10 md:py-12">
          <div className="container mx-auto px-4">
            <div className="grid gap-7 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
              <Fade bottom>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-[#9b7a00]">
                    Пошук роботи
                  </p>
                  <h2 className="mt-2 text-[32px] font-black uppercase leading-none tracking-[-0.04em] text-black md:text-[46px]">
                    Робота в Харкові без зайвої плутанини
                  </h2>
                  <p className="mt-4 max-w-[620px] text-[15px] font-medium leading-7 text-slate-600">
                    Yellow Hub допомагає людям у Харкові знайти швидку роботу,
                    підробіток, вакансії без досвіду або постійну зайнятість.
                    Можна порівняти оплату, формат, графік і відгукнутися на
                    пропозицію, яка підходить саме зараз.
                  </p>
                </div>
              </Fade>

              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  {
                    title: "Як відгукнутися на роботу?",
                    text: "Натисни «Відгукнутись» на потрібній пропозиції та залиш заявку, щоб команда могла зв'язатися з тобою.",
                  },
                  {
                    title: "Чи є робота без досвіду?",
                    text: "Так, частина пропозицій підходить без досвіду: достатньо пройти короткий інструктаж і виконати зрозумілий обсяг роботи.",
                  },
                ].map((item) => (
                  <Fade bottom key={item.title}>
                    <article className="rounded-[20px] border border-slate-200 bg-[#fffdf7] p-5">
                      <h3 className="text-[17px] font-black leading-tight text-black">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm font-medium leading-6 text-slate-600">
                        {item.text}
                      </p>
                    </article>
                  </Fade>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </section>
  );
};

export default WorkComponent;
